
import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import DupesSection from '../components/DupesSection';
import { Product, Dupe } from '../types';
import { ChevronDown, Filter, X } from 'lucide-react';

interface ShopProps {
  addToCart: (p: Product) => void;
  products: Product[];
  dupes: Dupe[];
}

type SortType = 'all' | 'available' | 'promo';

const BASE_CATEGORIES = [
  'Gels Antiseptiques',
  'Crèmes Mains',
  'Brumes Parfumées',
  'Room Sprays',
  'Lave-mains'
];

const Shop: React.FC<ShopProps> = ({ addToCart, products, dupes }) => {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [activeSort, setActiveSort] = useState<SortType>('all');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const catParam = searchParams.get('category');
    if (catParam) {
      setActiveCategory(catParam);
    }
  }, [searchParams]);

  const categories = useMemo(() => {
    const fetched = products.map((p) => p.category).filter(Boolean);
    const merged = Array.from(new Set([...BASE_CATEGORIES, ...fetched]));
    return ['Tous', ...merged];
  }, [products]);

  const processedProducts = useMemo(() => {
    let filtered = [...products];

    if (activeCategory !== 'Tous') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }

    if (activeSort === 'available') {
      filtered = filtered.filter(p => p.stock > 0);
    } else if (activeSort === 'promo') {
      filtered = filtered.filter((p) => {
        const normalizedLabel = (p.label || '').trim().toLowerCase();
        const hasDiscount = typeof p.originalPrice === 'number' && p.originalPrice > p.price;
        const hasPromoLabel = normalizedLabel === 'promo';
        return hasDiscount || hasPromoLabel;
      });
    }

    return filtered.sort((a, b) => {
      const stockA = a.stock > 0 ? 0 : 1;
      const stockB = b.stock > 0 ? 0 : 1;
      
      if (stockA !== stockB) return stockA - stockB;
      return a.name.localeCompare(b.name);
    });
  }, [products, activeCategory, activeSort]);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-brand-offwhite">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 border-b border-brand-black/5 pb-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-4 block">Exploration</span>
            <h1 className="font-serif text-4xl md:text-5xl mb-4 md:mb-0 text-brand-black">Les Collections</h1>
          </div>

          <div className="hidden md:flex flex-wrap justify-end gap-x-8 gap-y-4">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-[11px] uppercase tracking-widest font-bold luxury-transition ${
                  activeCategory === cat ? 'text-brand-gold border-b border-brand-gold' : 'text-brand-black/40 hover:text-brand-black'
                } pb-2`}
              >
                {cat}
              </button>
            ))}
          </div>

          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="md:hidden mt-8 flex justify-between items-center border p-4 border-brand-black/5 bg-brand-cream shadow-sm rounded-lg"
          >
            <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 text-brand-black">
              <Filter size={14} /> Filtrer & Trier
            </span>
            <ChevronDown size={16} className="text-brand-black" />
          </button>
        </div>

        <div className="flex gap-4 mb-12 overflow-x-auto pb-2 no-scrollbar">
          {[
            { id: 'all', label: 'Tout voir' },
            { id: 'available', label: 'En stock' },
            { id: 'promo', label: 'Promotions' }
          ].map(btn => (
            <button
              key={btn.id}
              onClick={() => setActiveSort(btn.id as SortType)}
              className={`px-6 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold whitespace-nowrap transition-all border ${
                activeSort === btn.id 
                ? 'bg-brand-black text-brand-offwhite border-brand-black' 
                : 'bg-brand-cream text-brand-black/40 border-brand-black/5 hover:border-brand-gold'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
          {processedProducts.map((product) => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onAddToCart={addToCart} 
            />
          ))}
        </div>

        {processedProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-brand-black/40 font-light italic">Aucun modèle ne correspond à vos critères.</p>
          </div>
        )}
      </div>

      {(activeCategory === 'Tous' || activeCategory === 'Brumes Parfumées') && (
        <div className="mt-24">
          <DupesSection dupes={dupes} />
        </div>
      )}

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-brand-black/40 backdrop-blur-sm md:hidden" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-[80%] bg-brand-offwhite p-8 shadow-2xl animate-in slide-in-from-right duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-12">
              <h3 className="font-serif text-2xl text-brand-black">Filtres</h3>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-brand-black"><X size={24} /></button>
            </div>
            <div className="space-y-10">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-brand-gold mb-6">Maison / Marque</p>
                <div className="flex flex-col gap-4 text-left">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setActiveCategory(cat); setIsMobileMenuOpen(false); }}
                      className={`text-left text-sm uppercase tracking-widest ${activeCategory === cat ? 'font-bold text-brand-black' : 'text-brand-black/40'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
